import React, { createRef, useEffect, useImperativeHandle } from 'react'

import {
  Card,
  type CardData,
  type CardLabels,
  type CardProps,
  type CardTimeFormat,
} from '@/components/molecules/Card/Card.tsx'

import { ConfirmDialog, type ConfirmDialogLabels } from '../ConfirmDialog/ConfirmDialog.tsx'
import {
  ConfirmTeamDialog,
  type ConfirmTeamDialogAction,
  type ConfirmTeamDialogData,
  type ConfirmTeamDialogLabels,
  type ConfirmTeamDialogOptions,
} from '../ConfirmTeamDialog/ConfirmTeamDialog.tsx'

export type CardAssignedTimeFormat = CardTimeFormat

export type CardAssignedOptions = ConfirmTeamDialogOptions

export interface CardAssignedLabels extends CardLabels {
  cancelConfirmDialog: ConfirmTeamDialogLabels
  putOnHoldConfirmDialog: ConfirmTeamDialogLabels
  waitingForTeam: string
  allocateManuallyConfirmDialog: ConfirmDialogLabels
}

export interface CardAssignedState extends Pick<CardProps, 'state' | 'size'> {
  highlighted?: boolean
}

export interface CardAssignedData extends Omit<CardData, 'team' | 'donation' | 'requestStatus'> {
  team: ConfirmTeamDialogData['team']
  pendingEvent?: 'cancelRequest' | 'allocateRequest' | 'putOnHoldRequest'
}

export interface CardAssignedAction {
  onCancelRequest: ConfirmTeamDialogAction['onConfirm']
  onAllocateManually: () => void
  onPutOnHold: ConfirmTeamDialogAction['onConfirm']
}

export interface CardAssignedProps
  extends
    React.HtmlHTMLAttributes<HTMLDivElement>,
    CardAssignedState,
    CardAssignedData,
    CardAssignedTimeFormat,
    CardAssignedAction {
  labels: CardAssignedLabels
  options: CardAssignedOptions
}

export const CardAssigned = React.forwardRef<HTMLDivElement, CardAssignedProps>(
  (
    {
      state,
      size,
      request,
      status,
      team: { usingMobileApp, ...team },
      locationLabel,
      pendingEvent,
      labels,
      options,
      highlighted,
      timeAgoFormatter,
      onCancelRequest,
      onAllocateManually,
      onPutOnHold,
      ...props
    },
    ref
  ) => {
    const cellRef = createRef<HTMLDivElement>()
    useImperativeHandle(ref, () => cellRef.current!, [cellRef])
    useEffect(() => {
      if (highlighted) {
        cellRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }, [state, cellRef, highlighted])
    return (
      <Card
        className={highlighted ? 'animate-pulse-gray' : ''}
        state={state}
        size={size}
        labels={labels}
        request={request}
        requestStatus="assigned"
        status={status}
        team={team}
        locationLabel={locationLabel}
        timeAgoFormatter={timeAgoFormatter}
        ref={ref}
        {...props}>
        {state === 'active' && (
          <>
            <div className="flex h-full gap-2.5">
              <ConfirmTeamDialog
                variant="critical"
                labels={labels.cancelConfirmDialog}
                options={options}
                team={{ usingMobileApp, ...team }}
                onConfirm={onCancelRequest}
                working={pendingEvent === 'cancelRequest'}
                disabled={!!pendingEvent && pendingEvent !== 'cancelRequest'}
              />
              <ConfirmTeamDialog
                labels={labels.putOnHoldConfirmDialog}
                options={options}
                team={{ usingMobileApp, ...team }}
                onConfirm={onPutOnHold}
                working={pendingEvent === 'putOnHoldRequest'}
                disabled={!!pendingEvent && pendingEvent !== 'putOnHoldRequest'}
              />
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-right text-base font-bold text-black">{labels.waitingForTeam}</span>
              <ConfirmDialog
                labels={labels.allocateManuallyConfirmDialog}
                onConfirm={onAllocateManually}
                working={pendingEvent === 'allocateRequest'}
                disabled={!!pendingEvent && pendingEvent !== 'allocateRequest'}
              />
            </div>
          </>
        )}
      </Card>
    )
  }
)
